import * as React from "react";
import { X } from "lucide-react";
import { Badge } from "./badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "./command";
import { cn } from "../../lib/utils";

export interface Option {
  label: string;
  value: string;
}

interface MultiSelectProps {
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isDarkMode?: boolean;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select items...",
  className,
  disabled = false,
  isDarkMode = false,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleUnselect = (value: string) => {
    onChange(selected.filter((s) => s !== value));
  };

  const handleSelect = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleSelectAll = () => {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map((option) => option.value));
    }
  };

  const isAllSelected = selected.length === options.length && options.length > 0;

  return (
    <div className={cn("relative", className)}>
      <div
        className={cn(
          "flex min-h-10 w-full flex-wrap gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          disabled && "cursor-not-allowed opacity-50",
          isDarkMode && "dark:bg-[#0d1117] dark:border-[#30363d] dark:focus-within:ring-[#f0883e]"
        )}
        onClick={() => !disabled && setOpen(true)}
      >
        {selected.map((value) => {
          const option = options.find((o) => o.value === value);
          return (
            <Badge
              key={value}
              variant="secondary"
              className="flex items-center gap-1 px-2 py-1 dark:bg-[#30363d] dark:text-[#f0f6fc]"
            >
              <span>{option?.label || value}</span>
              <button
                type="button"
                className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleUnselect(value);
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnselect(value);
                }}
                disabled={disabled}
              >
                <X className="h-3 w-3 text-muted-foreground hover:text-foreground dark:text-[#8b949e] dark:hover:text-[#f0f6fc]" />
              </button>
            </Badge>
          );
        })}
        <input
          ref={inputRef}
          placeholder={selected.length === 0 ? placeholder : ""}
          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground dark:placeholder:text-[#8b949e] min-w-[120px] dark:text-[#f0f6fc]"
          onFocus={() => setOpen(true)}
          disabled={disabled}
          readOnly
        />
      </div>

      {open && !disabled && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div className="absolute z-50 w-full mt-2 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in dark:bg-[#161b22] dark:border-[#30363d]">
            <Command className="w-full dark:bg-[#161b22]">
              <CommandInput 
                placeholder="Search..." 
                className="dark:text-[#f0f6fc] dark:placeholder:text-[#8b949e]"
              />
              <CommandEmpty className="dark:text-[#8b949e]">No results found.</CommandEmpty>
              <CommandGroup className="max-h-64 overflow-auto p-2">
                <CommandItem
                  onSelect={handleSelectAll}
                  className="cursor-pointer dark:text-[#c9d1d9] dark:hover:bg-[#21262d] dark:aria-selected:bg-[#21262d]"
                >
                  <div
                    className={cn(
                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary dark:border-[#30363d]",
                      isAllSelected
                        ? "[&_svg]:visible"
                        : "[&_svg]:invisible",
                      isAllSelected && "dark:bg-[#f0883e] dark:border-[#f0883e]"
                    )}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <span className="font-medium">Select All</span>
                </CommandItem>
                {options.map((option) => {
                  const isSelected = selected.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => handleSelect(option.value)}
                      className="cursor-pointer dark:text-[#c9d1d9] dark:hover:bg-[#21262d] dark:aria-selected:bg-[#21262d]"
                    >
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary dark:border-[#30363d]",
                          isSelected
                            ? "[&_svg]:visible"
                            : "[&_svg]:invisible",
                          isSelected && "dark:bg-[#f0883e] dark:border-[#f0883e]"
                        )}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-4 w-4"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </Command>
          </div>
        </>
      )}
    </div>
  );
}
